import { debounce, SxProps } from '@mui/material';
import { Theme } from '@mui/system';
import { AgGridReact } from 'ag-grid-react';
import React, { JSX } from 'react';
import { useIntl } from 'react-intl';

import { HBSelectProps } from '../../HBSelect/HBSelect';
import { MenuItemProps } from '../HBGridHeader/HBGridHeader';
import { HBGridMoreItems } from './components/HBGridMoreItems';
import { HBDataGridSearch, HBDataGridSearchProps } from './components/HBGridSearch/HBGridSearch';
import { HBGridStatus } from './components/HBGridStatus';
import { HBGridToolbarItem, HBGridToolbarItemProps } from './components/HBGridToolbarItem';
import { HBGridToolbarStyleRoot } from './HBGridToolbar.styles';
import { messages } from './messages';

export type HBDataGridToolbarCallbackProps = {
  type: 'create' | 'edit' | 'delete' | 'export' | 'search' | 'refresh' | 'more' | string;
};

export enum ChildPlaceEnum {
  first = 1,
  afterSearch = 2,
  afterRefresh = 3,
  afterStatus = 4,
  afterDelete = 5,
  afterEdit = 6,
  afterMenu = 7,
  last = 8,
}

export type HBDataGridToolbarProps = {
  items?: MenuItemProps[];
  onClick?: (props: HBDataGridToolbarCallbackProps) => void;
  onChange?: (value: number | string, type: 'search' | 'status') => void;
  children?: React.ReactNode;
  addProps?: HBGridToolbarItemProps;
  editProps?: HBGridToolbarItemProps;
  deleteProps?: HBGridToolbarItemProps;
  refreshProps?: HBGridToolbarItemProps;
  moreProps?: HBGridToolbarItemProps;
  searchProps?: HBDataGridSearchProps;
  statusProps?: Partial<HBSelectProps> & { show?: boolean };
  sx?: SxProps<Theme>;
  searchText?: string;
  agGridRef?: React.RefObject<AgGridReact>;
  childPlace?: ChildPlaceEnum;
};

export function HBDataGridToolbar(props: HBDataGridToolbarProps): JSX.Element {
  const {
    items,
    onClick,
    onChange,
    children,
    addProps,
    editProps,
    deleteProps,
    refreshProps,
    moreProps,
    sx,
    searchProps = { show: true },
    statusProps = { show: true },
    searchText,
    agGridRef,
    childPlace = ChildPlaceEnum.first,
  } = props;

  const { formatMessage } = useIntl();

  const handleSearch = (search: string) => {
    onChange?.(search, 'search');
  };

  return (
    <HBGridToolbarStyleRoot sx={sx}>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.first}>
        <HBGridToolbarItem
          icon="plus"
          tooltip={formatMessage(messages.create)}
          onClick={() => onClick?.({ type: 'create' })}
          {...addProps}
          sx={{
            color: (theme) =>
              addProps?.disabled ? theme.palette.grey[300] : theme.palette.primary.main,
          }}
        />
      </ChildWrapper>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterStatus}>
        <HBGridStatus {...{ statusProps, onChange, agGridRef }} />
      </ChildWrapper>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterSearch}>
        <HBDataGridSearch
          onSearch={debounce(handleSearch, 500)}
          openPosition="right"
          {...searchProps}
          searchText={searchText}
        />
      </ChildWrapper>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterEdit}>
        <HBGridToolbarItem
          icon="editAlt"
          tooltip={formatMessage(messages.edit)}
          onClick={() => onClick?.({ type: 'edit' })}
          {...editProps}
        />
      </ChildWrapper>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterDelete}>
        <HBGridToolbarItem
          icon="trashAlt"
          tooltip={formatMessage(messages.delete)}
          onClick={() => onClick?.({ type: 'delete' })}
          {...deleteProps}
        />
      </ChildWrapper>
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterRefresh}>
        <HBGridToolbarItem
          icon="sync"
          tooltip={formatMessage(messages.update)}
          onClick={() => onClick?.({ type: 'refresh' })}
          {...refreshProps}
        />
      </ChildWrapper>
      {Number(items?.length) > 0 && (
        <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.afterMenu}>
          <HBGridMoreItems {...{ moreProps, items }} />
        </ChildWrapper>
      )}
      <ChildWrapper child={children} hasChild={childPlace === ChildPlaceEnum.last} />
    </HBGridToolbarStyleRoot>
  );
}

type ChildWrapperProps = {
  hasChild: boolean;
  children?: React.ReactNode;
  child: React.ReactNode;
};

function ChildWrapper(props: ChildWrapperProps) {
  const { hasChild, children, child } = props;

  return (
    <>
      {hasChild && child}
      {children}
    </>
  );
}

export type { HBDataGridSearchProps, HBGridToolbarItemProps };
export { HBDataGridSearch, HBGridToolbarItem };
